import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService, type Post, type FeedParams, type CreatePostData } from '../lib/api';
import { useAuth } from './useAuth';

export const usePosts = (params?: FeedParams) => {
  return useQuery({
    queryKey: ['posts', params],
    queryFn: () => apiService.getPosts(params),
    staleTime: 30 * 1000, // 30 seconds - shorter to show fresh poll votes
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchInterval: 5000, // Live poll counts; pauses in background (refetchIntervalInBackground defaults false)
  });
};

export const useUserPosts = (params?: FeedParams) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['userPosts', params, user?.id],
    queryFn: () => apiService.getUserPosts(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!user?.id,
  });
};

export const useSavedPosts = (params?: FeedParams) => {
  return useQuery({
    queryKey: ['savedPosts', params],
    queryFn: async () => {
      // Fallback: Get all posts and filter by savedByUser flag
      console.log('💾 Filtering posts for saved posts...');
      const allPosts = await apiService.getPosts(params);
      const savedPosts = allPosts.filter(post => post.savedByUser === true);
      console.log(`💾 Found ${savedPosts.length} saved posts`);
      return savedPosts;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useInfinitePosts = (limit: number = 20) => {
  return useInfiniteQuery({
    queryKey: ['posts', 'infinite'],
    queryFn: ({ pageParam = 0 }) => apiService.getPosts({ page: pageParam, limit }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has fewer posts than the limit, we've reached the end
      if (lastPage.length < limit) {
        return undefined;
      }
      // Otherwise, return the next page number (0-based)
      return allPages.length;
    },
    staleTime: 30 * 1000, // 30 seconds - shorter to show fresh poll votes
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchInterval: 5000, // Live poll counts; pauses in background (refetchIntervalInBackground defaults false)
  });
};

export const useInfiniteUserPosts = (limit: number = 50) => {
  const { user } = useAuth();

  return useInfiniteQuery({
    queryKey: ['userPosts', 'infinite', user?.id],
    queryFn: ({ pageParam = 0 }) => apiService.getUserPosts({ page: pageParam, limit }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has fewer posts than the limit, we've reached the end
      if (lastPage.length < limit) {
        return undefined;
      }
      // Otherwise, return the next page number (0-based)
      return allPages.length;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!user?.id,
  });
};

export const useCreatePost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreatePostData) => apiService.createPost(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts', 'infinite'] });
    },
  });
};

export const useUpdatePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, data }: { postId: number; data: CreatePostData }) => apiService.updatePost(postId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts', 'infinite'] });
      queryClient.invalidateQueries({ queryKey: ['drafts'] });
    },
  });
};

export const useLikePost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ postId, isLiked }: { postId: number; isLiked: boolean }) =>
      isLiked ? apiService.unlikePost(postId) : apiService.likePost(postId),
    onMutate: async ({ postId, isLiked }) => {
      await queryClient.cancelQueries({ queryKey: ['posts'] });
      
      const previousPosts = queryClient.getQueriesData({ queryKey: ['posts'] });
      
      queryClient.setQueriesData({ queryKey: ['posts'] }, (old: any) => {
        if (!old) return old;
        
        // Handle infinite query structure (has pages array)
        if (old.pages && Array.isArray(old.pages)) {
          return {
            ...old,
            pages: old.pages.map((page: Post[]) => {
              if (Array.isArray(page)) {
                return page.map((post) =>
                  post.id === postId
                    ? {
                        ...post,
                        upvotedByUser: !isLiked,
                        upvoteCount: isLiked ? post.upvoteCount - 1 : post.upvoteCount + 1,
                      }
                    : post
                );
              }
              return page;
            })
          };
        }
        
        // Handle regular array of posts
        if (Array.isArray(old)) {
          return old.map((post) =>
            post.id === postId
              ? {
                  ...post,
                  upvotedByUser: !isLiked,
                  upvoteCount: isLiked ? post.upvoteCount - 1 : post.upvoteCount + 1,
                }
              : post
          );
        }
        
        return old;
      });
      
      return { previousPosts };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousPosts) {
        context.previousPosts.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts', 'infinite'] });
      queryClient.invalidateQueries({ queryKey: ['userPosts'] });
    },
  });
};

export const useSavePost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ postId, isSaved }: { postId: number; isSaved: boolean }) =>
      isSaved ? apiService.unsavePost(postId) : apiService.savePost(postId),
    onMutate: async ({ postId, isSaved }) => {
      console.log('🔄 Save optimistic update:', { postId, currentSaved: isSaved, willBeSaved: !isSaved });
      
      await queryClient.cancelQueries({ queryKey: ['posts'] });
      await queryClient.cancelQueries({ queryKey: ['userPosts'] });
      await queryClient.cancelQueries({ queryKey: ['savedPosts'] });
      
      const previousData = {
        posts: queryClient.getQueriesData({ queryKey: ['posts'] }),
        userPosts: queryClient.getQueriesData({ queryKey: ['userPosts'] }),
        savedPosts: queryClient.getQueriesData({ queryKey: ['savedPosts'] })
      };
      
      // Update all relevant queries - handle different data structures
      const updatePostInQuery = (old: any) => {
        if (!old) return old;
        
        // Handle infinite query structure (has pages array)
        if (old.pages && Array.isArray(old.pages)) {
          return {
            ...old,
            pages: old.pages.map((page: Post[]) => {
              if (Array.isArray(page)) {
                return page.map((post) =>
                  post.id === postId
                    ? { ...post, savedByUser: !isSaved }
                    : post
                );
              }
              return page;
            })
          };
        }
        
        // Handle regular array of posts
        if (Array.isArray(old)) {
          return old.map((post) =>
            post.id === postId
              ? { ...post, savedByUser: !isSaved }
              : post
          );
        }
        
        return old;
      };
      
      queryClient.setQueriesData({ queryKey: ['posts'] }, updatePostInQuery);
      queryClient.setQueriesData({ queryKey: ['userPosts'] }, updatePostInQuery);
      queryClient.setQueriesData({ queryKey: ['savedPosts'] }, updatePostInQuery);
      
      console.log('✅ Optimistic update applied');
      return { previousData };
    },
    onError: (err, _variables, context) => {
      console.error('❌ Save error - reverting optimistic update:', err);
      if (context?.previousData) {
        context.previousData.posts.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
        context.previousData.userPosts.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
        context.previousData.savedPosts.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      console.log('🔄 Save settled - invalidating queries');
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts', 'infinite'] });
      queryClient.invalidateQueries({ queryKey: ['savedPosts'] });
    },
  });
};

export const useDeletePost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (postId: number) => apiService.deletePost(postId),
    onMutate: async (postId) => {
      console.log('🗑️ Delete optimistic update for post:', postId);
      
      // Cancel ongoing queries to prevent conflicts
      await queryClient.cancelQueries({ queryKey: ['posts'] });
      await queryClient.cancelQueries({ queryKey: ['userPosts'] });
      await queryClient.cancelQueries({ queryKey: ['savedPosts'] });
      
      // Store previous data for rollback
      const previousData = {
        posts: queryClient.getQueriesData({ queryKey: ['posts'] }),
        userPosts: queryClient.getQueriesData({ queryKey: ['userPosts'] }),
        savedPosts: queryClient.getQueriesData({ queryKey: ['savedPosts'] })
      };
      
      // Optimistically remove the post from all queries
      const removePostFromQuery = (old: any) => {
        if (!old) return old;
        
        // Handle infinite query structure
        if (old.pages && Array.isArray(old.pages)) {
          return {
            ...old,
            pages: old.pages.map((page: Post[]) => {
              if (Array.isArray(page)) {
                return page.filter((post) => post.id !== postId);
              }
              return page;
            })
          };
        }
        
        // Handle regular array
        if (Array.isArray(old)) {
          return old.filter((post) => post.id !== postId);
        }
        
        return old;
      };
      
      queryClient.setQueriesData({ queryKey: ['posts'] }, removePostFromQuery);
      queryClient.setQueriesData({ queryKey: ['userPosts'] }, removePostFromQuery);
      queryClient.setQueriesData({ queryKey: ['savedPosts'] }, removePostFromQuery);
      
      console.log('✅ Post removed from UI optimistically');
      return { previousData };
    },
    onError: (err, _postId, context) => {
      console.error('❌ Delete error - restoring post:', err);
      if (context?.previousData) {
        context.previousData.posts.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
        context.previousData.userPosts.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
        context.previousData.savedPosts.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      console.log('🔄 Delete settled - refreshing queries');
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts', 'infinite'] });
      queryClient.invalidateQueries({ queryKey: ['userPosts'] });
      queryClient.invalidateQueries({ queryKey: ['savedPosts'] });
    },
  });
};

export const useVotePoll = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ pollId, optionId }: { pollId: number; optionId: number }) =>
      apiService.votePoll(pollId, optionId),
    onSuccess: (pollResults, { pollId }) => {
      console.log('🗳️ Poll vote success, updating cache with results:', pollResults);
      
      // Helper function to update poll in posts
      const updatePollInPosts = (posts: any[]) => {
        return posts.map((post: any) => {
          if (post.poll?.id === pollId) {
            console.log('📊 Updating poll in post:', post.id, 'with results:', pollResults);
            return {
              ...post,
              poll: {
                ...post.poll,
                results: pollResults
              }
            };
          }
          return post;
        });
      };
      
      // Update all posts queries with different data structures
      queryClient.setQueriesData({ queryKey: ['posts'] }, (oldData: any) => {
        if (!oldData) return oldData;
        
        // Handle infinite query structure (has pages array)
        if (oldData.pages && Array.isArray(oldData.pages)) {
          console.log('🔄 Updating infinite query structure');
          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => {
              // Check if page has nested data array
              if (page.data && Array.isArray(page.data)) {
                return {
                  ...page,
                  data: updatePollInPosts(page.data)
                };
              }
              // Page is directly an array of posts
              if (Array.isArray(page)) {
                return updatePollInPosts(page);
              }
              return page;
            })
          };
        }
        
        // Handle regular array of posts
        if (Array.isArray(oldData)) {
          console.log('🔄 Updating simple array structure');
          return updatePollInPosts(oldData);
        }
        
        return oldData;
      });
      
      // Also invalidate as fallback to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts', 'infinite'] });
      queryClient.invalidateQueries({ queryKey: ['userPosts'] });
    },
  });
};